import {
  Alert,
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Radio,
  Select,
  Space,
  Spin,
} from 'antd'
import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  commentsRules,
  deviceCodeRules,
  deviceFriendlyNameRules,
  enrollmentDateRules,
  hexnodeRefIdRules,
  hologramIccidRules,
  hologramSimIdRules,
  imeiNumbersRules,
  statusRules,
  vesselRules,
} from './deviceValidation'

const { Option } = Select
const { TextArea } = Input

const RegisterDeviceDrawer = ({
  error,
  isSaving,
  onClose,
  onRegister,
  requestingShipOptions,
  setRegisterDeviceError,
  shipOptions,
  visible,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()

  const handleClose = () => {
    form.resetFields()
    setRegisterDeviceError(null)
    onClose()
  }

  const handleFinish = values => {
    const payload = {
      comments: values.comments?.trim() ?? null,
      deviceCode: values.deviceCodeName?.trim() ?? null,
      enrolledDate: values.enrollmentDate
        ? values.enrollmentDate.format('YYYY-MM-DDTHH:mm:ss')
        : null,
      hexnodeId: values.hexnodeRefId ? Number(values.hexnodeRefId) : null,
      hologramIccid: values.hologramIccid ? Number(values.hologramIccid) : null,
      hologramId: values.hologramSimId ? Number(values.hologramSimId) : null,
      imeiNumbers: values.imeiNumbers?.trim() ?? null,
      name: values.deviceFriendlyName?.trim() ?? null,
      shipId: values.vessel ?? null,
      status: values.status === 'active',
    }

    onRegister({ onSuccess: handleClose, payload })
  }

  return (
    <Drawer
      destroyOnClose
      onClose={handleClose}
      open={visible}
      title={t('deviceManagement.registerDrawer.title')}
      width={480}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button disabled={isSaving} onClick={handleClose}>
              {t('deviceManagement.registerDrawer.cancel')}
            </Button>

            <Button
              disabled={isSaving}
              loading={isSaving}
              onClick={() => form.submit()}
              type="primary"
            >
              {t('deviceManagement.registerDrawer.register')}
            </Button>
          </Space>
        </div>
      }
    >
      <Spin spinning={isSaving}>
        {error && (
          <Alert
            closable
            description={error}
            message={t('deviceManagement.registerDrawer.errorTitle')}
            onClose={() => setRegisterDeviceError(null)}
            showIcon
            style={{ marginBottom: 16 }}
            type="error"
          />
        )}
        <Form
          form={form}
          initialValues={{ status: 'active' }}
          layout="vertical"
          name="register-device"
          onFinish={handleFinish}
        >
          {/* Vessel */}
          <Form.Item
            label={t('deviceManagement.registerDrawer.vessel')}
            name="vessel"
            required
            rules={vesselRules(t)}
          >
            <Select
              loading={requestingShipOptions}
              showSearch
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
              notFoundContent={
                requestingShipOptions ? (
                  <Spin size="small" />
                ) : (
                  t('deviceManagement.registerDrawer.noVesselsFound')
                )
              }
              placeholder={t(
                'deviceManagement.registerDrawer.vesselPlaceholder'
              )}
            >
              {(shipOptions || []).map(vessel => (
                <Option key={vessel.id} value={vessel.id}>
                  {vessel.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Device Code Name */}
          <Form.Item
            label={t('deviceManagement.registerDrawer.deviceCodeName')}
            name="deviceCodeName"
            required
            rules={deviceCodeRules(t)}
          >
            <Input />
          </Form.Item>

          {/* Device Friendly Name */}
          <Form.Item
            label={t('deviceManagement.registerDrawer.deviceFriendlyName')}
            name="deviceFriendlyName"
            required
            rules={deviceFriendlyNameRules(t)}
          >
            <Input />
          </Form.Item>

          {/* Enrollment Date */}
          <Form.Item
            label={t('deviceManagement.registerDrawer.enrollmentDate')}
            name="enrollmentDate"
            required
            rules={enrollmentDateRules(t)}
          >
            <DatePicker format="DD-MM-YYYY" />
          </Form.Item>

          {/* Status */}
          <Form.Item
            label={t('deviceManagement.registerDrawer.status')}
            name="status"
            required
            rules={statusRules(t)}
          >
            <Radio.Group>
              <Radio value="active">
                {t('deviceManagement.registerDrawer.statusActive')}
              </Radio>
              <Radio value="inactive">
                {t('deviceManagement.registerDrawer.statusInactive')}
              </Radio>
            </Radio.Group>
          </Form.Item>

          {/* IMEI */}
          <Form.Item
            label={t('deviceManagement.registerDrawer.imeiNumbers')}
            name="imeiNumbers"
            required
            rules={imeiNumbersRules(t)}
          >
            <Input />
          </Form.Item>

          {/* Hexnode Ref ID */}
          <Form.Item
            label={t('deviceManagement.registerDrawer.hexnodeRefId')}
            name="hexnodeRefId"
            required
            rules={hexnodeRefIdRules(t)}
          >
            <Input />
          </Form.Item>

          {/* Hologram ICCID */}
          <Form.Item
            label={t('deviceManagement.registerDrawer.hologramIccid')}
            name="hologramIccid"
            required
            rules={hologramIccidRules(t)}
          >
            <Input />
          </Form.Item>

          {/* Hologram SIM ID */}
          <Form.Item
            label={t('deviceManagement.registerDrawer.hologramSimId')}
            name="hologramSimId"
            required
            rules={hologramSimIdRules(t)}
          >
            <Input />
          </Form.Item>

          {/* Comments */}
          <Form.Item
            label={t('deviceManagement.registerDrawer.comments')}
            name="comments"
            required
            rules={commentsRules(t)}
          >
            <TextArea rows={4} />
          </Form.Item>
        </Form>
      </Spin>
    </Drawer>
  )
}

RegisterDeviceDrawer.propTypes = {
  error: PropTypes.string,
  isSaving: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onRegister: PropTypes.func.isRequired,
  requestingShipOptions: PropTypes.bool,
  setRegisterDeviceError: PropTypes.func.isRequired,
  shipOptions: PropTypes.array,
  visible: PropTypes.bool,
}

RegisterDeviceDrawer.defaultProps = {
  error: null,
  isSaving: false,
  requestingShipOptions: false,
  shipOptions: [],
  visible: false,
}

export default RegisterDeviceDrawer
